import React, {useEffect, useRef, useState} from "react";
import styles from "./settingPopover.module.css";
import {
    SettingOutlined,
    VerticalAlignBottomOutlined,
    VerticalAlignMiddleOutlined,
    VerticalAlignTopOutlined
} from "@ant-design/icons";
import {Checkbox, Popover, Tooltip} from "antd";
import {ColumnsType} from "antd/lib/table/interface";
import {PopoverProps} from "antd/lib/popover";

export interface ColumnProps {
    title: string
    dataIndex: any
    checked: boolean,
    fixed: string,
}

interface SettingPopoverProps extends PopoverProps {
    columns: ColumnsType<any> | undefined
    onChange: (top: ColumnProps[], mid: ColumnProps[], bottom: ColumnProps[]) => void
}

function PopoverItem(props: {
    dataIndex: string,
    title: string,
    fixed: string
    onPinClick: (dataIndex: string, fixed: string) => void
}) {
    const {dataIndex, title, fixed, onPinClick} = props

    return (
        <div className={styles.columnSettingCheck}
             key={dataIndex}>
            <Checkbox value={dataIndex}>{title}</Checkbox>
            {fixed !== 'left' && (<Tooltip title={'Pin to start'}><VerticalAlignTopOutlined
                onClick={() => onPinClick(dataIndex, 'left')}/></Tooltip>)}
            {(fixed !== '') && (
                <Tooltip title={'Unpinned'}><VerticalAlignMiddleOutlined
                    onClick={() => onPinClick(dataIndex, '')}/></Tooltip>)}
            {fixed !== 'right' && (<Tooltip title={'Pin to end'}><VerticalAlignBottomOutlined
                onClick={() => onPinClick(dataIndex, 'right')}/></Tooltip>)}
        </div>
    )
}

function SettingPopover(props: SettingPopoverProps) {

    const {columns, onChange, ...otherProps} = props
    const [columnsList, setColumnsList] = useState<ColumnProps[]>([])

    useEffect(() => {
        if (columns) {
            let cl = columns.map((item: any) => {
                const exist = columnsList.find(i => i.dataIndex === item.dataIndex)
                if (exist) {
                    return {...item, fixed: exist.fixed, checked: exist.checked}
                } else {
                    return {...item, fixed: item.fixed ? item.fixed : '', checked: true}
                }
            })
            onColumnsChange(cl)
        }
    }, [columns])

    const checked = columnsList.filter(item => item.checked).map(item => item.dataIndex)
    const titleChecked = checked.length === columnsList.length
    const titleIndeterminate = checked.length > 0 && checked.length < columnsList.length
    const columnTitle = (
        <div style={{padding: '5px 0'}}>
            <Checkbox onChange={onColumnTitleClick} checked={titleChecked}
                      indeterminate={titleIndeterminate}>Column Display</Checkbox>
        </div>
    )

    function columnContentRender(cols: ColumnProps[]) {
        return (
            <Checkbox.Group value={checked}
                            style={{maxHeight: '90vh', overflow: 'scroll'}}
                            onChange={onColumnContentClick}>
                {cols.length > 0 && columnRender(cols)}
            </Checkbox.Group>
        )
    }

    function columnRender(columns: ColumnProps[]) {
        const map: { [key: string]: string } = {'left': 'Fixed the left', '': 'Not Fixed', 'right': 'Fixed the end'}
        return Object.keys(map).map(key => {
            const title = map[key]
            const cols = columns.filter(item => item.fixed === key)
            return cols.length > 0 ? (
                <div key={key}>
                    <div className={styles.columnSettingTitle}>{title}</div>
                    {cols.map((item, index) => (
                        <PopoverItem key={item.dataIndex}
                                     dataIndex={item.dataIndex}
                                     title={item.title}
                                     fixed={item.fixed}
                                     onPinClick={onPinClick}/>
                    ))}
                </div>
            ) : null
        })
    }

    function onColumnTitleClick() {
        if (checked.length > 0) {
            onColumnsChange(columnsList.map(i => ({...i, checked: false})))
        } else {
            onColumnsChange(columnsList.map(i => ({...i, checked: true})))
        }
    }

    function onColumnContentClick(checkedValue: any) {
        onColumnsChange(columnsList.map(item => ({...item, checked: checkedValue.indexOf(item.dataIndex) !== -1})))
    }

    function onPinClick(dataIndex: string, fixed: string) {
        onColumnsChange(columnsList.map(item => ({
            ...item,
            fixed: item.dataIndex === dataIndex ? fixed : item.fixed
        })))
    }

    function onColumnsChange(newColumns: ColumnProps[]) {
        const newTop = newColumns.filter(item => item.checked && item.fixed === 'left')
        const newMid = newColumns.filter(item => item.checked && item.fixed === '')
        const newBottom = newColumns.filter(item => item.checked && item.fixed === 'right')
        setColumnsList([...newColumns])
        onChange(newTop || [], newMid || [], newBottom || [])
    }

    return (
        <Popover overlayClassName={styles.columnSettingPopoverOverlay}
                 title={columnTitle} content={columnContentRender(columnsList)}
                 trigger={['click']}
                 placement={'left'}
                 {...otherProps}
        >
            <SettingOutlined/>
        </Popover>
    )
}

export default SettingPopover
